import { type JSX, useEffect, useState } from 'react';

import type { ProfileSchedule } from '@schema/profile.js';

import type { BuilderStore } from '../state.js';

export interface SchedulesEditorProps {
  store: BuilderStore;
}

type DropPosition = 'above' | 'below';
interface DropTarget {
  id: string;
  position: DropPosition;
}

const DAYS: readonly { value: 0 | 1 | 2 | 3 | 4 | 5 | 6; short: string; long: string }[] = [
  { value: 0, short: 'S', long: 'Sun' },
  { value: 1, short: 'M', long: 'Mon' },
  { value: 2, short: 'T', long: 'Tue' },
  { value: 3, short: 'W', long: 'Wed' },
  { value: 4, short: 'T', long: 'Thu' },
  { value: 5, short: 'F', long: 'Fri' },
  { value: 6, short: 'S', long: 'Sat' },
];

/**
 * Time-windowed profile activation editor (Pro feature: `scheduledProfiles`).
 *
 * Each row is one ProfileSchedule. At runtime the array is scanned in order,
 * first match wins, fall-through is the config's `defaultProfileId`. That
 * "first match wins" rule is *why* this editor surfaces up/down reorder —
 * order is load-bearing, not cosmetic.
 *
 * Validity gotchas the Zod parse flags downstream:
 *   • daysOfWeek must have at least one entry → 0-day rows go red in
 *     the Validation tab.
 *   • profileId must reference an entry in `profiles[]` — the dropdown
 *     only offers real options, so this only breaks on imported JSON.
 *
 * The builder is single-profile in v0; the profile dropdown stays so the
 * editor doesn't need a rewrite when multi-profile lands.
 */
export function SchedulesEditor({ store }: SchedulesEditorProps): JSX.Element {
  const { schedules, profiles } = store.config;
  const defaultProfileId = store.config.defaultProfileId;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  // "What's active right now" preview. Ticks each 30s so the banner
  // and the per-row badge stay accurate as windows open/close while
  // the admin is editing. Schedule windows are minute-granular.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const h = window.setInterval(() => setNow(new Date()), 30_000);
    return () => {
      window.clearInterval(h);
    };
  }, []);

  // Mirrors the runtime's resolveActiveProfile (src/runtime/schedule.ts):
  // first schedule whose window matches AND whose profile resolves wins;
  // otherwise fall through to defaultProfileId.
  const profileIds = new Set(profiles.map((p) => p.id));
  const activeIdx = firstActiveScheduleIndex(schedules, profileIds, now);
  const activeProfileId = activeIdx >= 0 ? schedules[activeIdx]!.profileId : defaultProfileId;
  const activeProfile = profiles.find((p) => p.id === activeProfileId) ?? null;

  function update(idx: number, patch: Partial<ProfileSchedule>): void {
    const next = schedules.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    store.patchSchedules(next);
  }

  function remove(idx: number): void {
    store.patchSchedules(schedules.filter((_, i) => i !== idx));
  }

  function move(idx: number, direction: 'up' | 'down'): void {
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= schedules.length) return;
    const next = [...schedules];
    const [a, b] = [next[idx]!, next[swap]!];
    next[idx] = b;
    next[swap] = a;
    store.patchSchedules(next);
  }

  /**
   * Drop a dragged schedule at a specific index. Mirrors the tile-grid
   * moveTileTo: same-slot drops are no-ops so a jittery drop doesn't
   * pollute the undo stack with an effective no-change entry.
   */
  function moveTo(fromIdx: number, toIdx: number): void {
    if (fromIdx === -1) return;
    if (toIdx < 0 || toIdx > schedules.length) return;
    if (toIdx === fromIdx || toIdx === fromIdx + 1) return;
    const next = [...schedules];
    const [item] = next.splice(fromIdx, 1);
    const insertAt = toIdx > fromIdx ? toIdx - 1 : toIdx;
    next.splice(insertAt, 0, item!);
    store.patchSchedules(next);
  }

  function handleDrop(): void {
    if (draggingId !== null && dropTarget !== null) {
      const fromIdx = schedules.findIndex((s) => s.id === draggingId);
      const toIdx = schedules.findIndex((s) => s.id === dropTarget.id);
      if (toIdx !== -1) {
        const insertAt = dropTarget.position === 'above' ? toIdx : toIdx + 1;
        moveTo(fromIdx, insertAt);
      }
    }
    setDraggingId(null);
    setDropTarget(null);
  }

  function add(): void {
    const head = profiles[0];
    if (!head) return;
    const fresh: ProfileSchedule = {
      id: crypto.randomUUID(),
      profileId: head.id,
      daysOfWeek: [1, 2, 3, 4, 5], // weekdays
      startTime: '09:00',
      endTime: '17:00',
    };
    store.patchSchedules([...schedules, fresh]);
  }

  function toggleDay(idx: number, day: 0 | 1 | 2 | 3 | 4 | 5 | 6): void {
    const current = schedules[idx];
    if (!current) return;
    const has = current.daysOfWeek.includes(day);
    const nextDays = has
      ? current.daysOfWeek.filter((d) => d !== day)
      : [...current.daysOfWeek, day].sort((a, b) => a - b);
    update(idx, { daysOfWeek: nextDays });
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Schedules</h2>
        <p className="editor__sub">
          Switch profiles by time of day. First match wins; fall-through is{' '}
          <code>defaultProfileId</code>. Requires the{' '}
          <strong>scheduledProfiles</strong> Pro feature to take effect on-device.{' '}
          <a href="https://mobilauncher.com/docs/customize#schedules" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
            Read the docs →
          </a>
        </p>
      </header>

      <div className="schedule-now" data-testid="schedule-now">
        <span className="schedule-now__clock">
          {now.toLocaleString(undefined, {
            weekday: 'short',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
        <span className="schedule-now__sep">→</span>
        <span className="schedule-now__profile">
          {activeProfile === null ? '—' : activeProfile.name}
        </span>
        <span className="schedule-now__source">
          {activeIdx >= 0
            ? `via schedule #${String(activeIdx + 1)}`
            : 'default profile (no schedule active)'}
        </span>
      </div>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Schedules ({schedules.length})</legend>

        {schedules.length === 0 && (
          <p className="schedule-list__empty">
            No schedules yet — the launcher will always show the default profile.
          </p>
        )}

        <ul
          className="schedule-list"
          onDragEnd={() => {
            setDraggingId(null);
            setDropTarget(null);
          }}
        >
          {schedules.map((s, idx) => {
            const isDragging = draggingId === s.id;
            const isTarget = dropTarget?.id === s.id;
            const dropPos = isTarget ? dropTarget.position : null;
            return (
            <li
              key={s.id}
              className={`dnd-row${isDragging ? ' dnd-row--dragging' : ''}${
                isTarget ? ` dnd-row--drop-${dropPos}` : ''
              }`}
              onDragOver={(e) => {
                if (draggingId === null || draggingId === s.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const rect = e.currentTarget.getBoundingClientRect();
                const midY = rect.top + rect.height / 2;
                const position: DropPosition = e.clientY < midY ? 'above' : 'below';
                if (dropTarget?.id !== s.id || dropTarget.position !== position) {
                  setDropTarget({ id: s.id, position });
                }
              }}
              onDragLeave={(e) => {
                const next = e.relatedTarget as Node | null;
                if (!e.currentTarget.contains(next)) {
                  if (dropTarget?.id === s.id) setDropTarget(null);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop();
              }}
            >
              <article className="schedule-card" data-testid={`schedule-${s.id}`}>
                <header className="schedule-card__head">
                  <span
                    className="schedule-card__grip"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', s.id);
                      const card = e.currentTarget.closest('.schedule-card');
                      if (card instanceof HTMLElement) {
                        e.dataTransfer.setDragImage(card, 16, 16);
                      }
                      setDraggingId(s.id);
                    }}
                    title="Drag to reorder"
                    aria-label="Drag to reorder schedule"
                    role="button"
                  >
                    ⋮⋮
                  </span>
                  <span className="schedule-card__index">#{idx + 1}</span>
                  {idx === activeIdx && (
                    <span className="schedule-card__active" data-testid="schedule-active-badge">
                      ● Active now
                    </span>
                  )}
                  <div className="schedule-card__buttons">
                    <button
                      type="button"
                      className="tile-card__btn"
                      onClick={() => move(idx, 'up')}
                      disabled={idx === 0}
                      aria-label="Move up"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="tile-card__btn"
                      onClick={() => move(idx, 'down')}
                      disabled={idx === schedules.length - 1}
                      aria-label="Move down"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="tile-card__btn tile-card__btn--danger"
                      onClick={() => remove(idx)}
                      aria-label="Remove schedule"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                </header>

                <div className="schedule-card__body">
                  <label className="field">
                    <span className="field__label">Profile</span>
                    <select
                      className="field__input"
                      value={s.profileId}
                      onChange={(e) => update(idx, { profileId: e.target.value })}
                    >
                      {profiles.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <span className="field__hint">
                      Profile to activate while this window is open.
                    </span>
                  </label>

                  <div className="schedule-card__days">
                    <span className="field__label">Days of week</span>
                    <div className="schedule-card__day-pills" role="group" aria-label="Days of week">
                      {DAYS.map((d) => {
                        const active = s.daysOfWeek.includes(d.value);
                        return (
                          <button
                            type="button"
                            key={d.value}
                            aria-pressed={active}
                            aria-label={d.long}
                            title={d.long}
                            className={`day-pill${active ? ' day-pill--active' : ''}`}
                            onClick={() => toggleDay(idx, d.value)}
                          >
                            {d.short}
                          </button>
                        );
                      })}
                    </div>
                    {s.daysOfWeek.length === 0 && (
                      <span className="field__hint field__hint--warn">
                        Pick at least one day — schema rejects an empty list.
                      </span>
                    )}
                  </div>

                  <div className="schedule-card__times">
                    <label className="field">
                      <span className="field__label">Start</span>
                      <input
                        type="time"
                        className="field__input field__input--time"
                        value={s.startTime}
                        onChange={(e) => update(idx, { startTime: e.target.value })}
                      />
                    </label>
                    <label className="field">
                      <span className="field__label">End</span>
                      <input
                        type="time"
                        className="field__input field__input--time"
                        value={s.endTime}
                        onChange={(e) => update(idx, { endTime: e.target.value })}
                      />
                    </label>
                  </div>
                </div>
              </article>
            </li>
            );
          })}
        </ul>

        <div className="add-tile">
          <span className="add-tile__label">Add schedule</span>
          <div className="add-tile__buttons">
            <button
              type="button"
              className="add-tile__btn"
              onClick={add}
              disabled={profiles.length === 0}
            >
              + Schedule
            </button>
          </div>
        </div>
      </fieldset>
    </div>
  );
}

/* ───── Active-schedule resolution (mirrors src/runtime/schedule.ts) ─────
 * Kept local so the portal doesn't depend on the runtime package. If the
 * runtime's matching rules change, update both. */

/** Index of the first schedule whose window matches `now` and whose
 *  profile still exists; -1 means fall through to the default profile. */
function firstActiveScheduleIndex(
  schedules: readonly ProfileSchedule[],
  profileIds: ReadonlySet<string>,
  now: Date,
): number {
  for (let i = 0; i < schedules.length; i++) {
    const s = schedules[i]!;
    if (scheduleMatches(s, now) && profileIds.has(s.profileId)) return i;
  }
  return -1;
}

function scheduleMatches(schedule: ProfileSchedule, now: Date): boolean {
  if (!schedule.daysOfWeek.includes(now.getDay())) return false;
  const minutes = now.getHours() * 60 + now.getMinutes();
  const start = parseHHmm(schedule.startTime);
  const end = parseHHmm(schedule.endTime);
  if (start === end) return false; // zero-duration window
  if (start < end) return minutes >= start && minutes < end;
  // Wrap-around (e.g. 20:00 → 06:00): after start OR before end.
  return minutes >= start || minutes < end;
}

function parseHHmm(s: string): number {
  const [h, m] = s.split(':');
  return Number(h) * 60 + Number(m);
}
